import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { Detalle } from "../components/Detail";
import { Historial } from "../components/History";
import { useParams, useSearchParams, useNavigate } from "react-router";
import { PageTabs } from "@/components/ui/page-tabs";
import {
  useGetProcess,
  useUpdateProcess,
  useArchiveProcess,
} from "@/queries/process";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  useGetMainFlowsByProcessId,
  useSendFlowToApproval,
} from "@/queries/flows/flows.api";
import { EditProcessModal } from "../components/EditProcessModal";
import DuplicateProcessModal from "../components/DuplicateProcessModal";
import ArchiveProcessModal from "../components/ArchiveProcessModal";
import ConfirmUnArchiveModal from "../components/ConfirmUnArchiveModal";
import SendResolutionModal from "../components/SendResolutionModal";
import { useQueryClient } from "@tanstack/react-query";
import { type ProcessStatus, STATUS_CONFIG } from "../constants";
import { useUpdateFlowRevision } from "@/queries/flows/flows.api";
import type { FlowsType } from "@/types/entities/flows";
import { useReverb } from "@/hooks/use-reverb";

export const DetalleProceso = () => {
  const { hasPermission } = useAuthorization();
  const queryClient = useQueryClient();
  const canCreate = hasPermission(Permissions.processes.create);
  const canRequestReview = hasPermission(Permissions.flows["request-review"]);
  const canPerformReview = hasPermission(Permissions.flows["perform-review"]);
  const [allFlowsInVersion, setAllFlowsInVersion] = useState<FlowsType[]>([]);
  const [activeTab, setActiveTab] = useState<"detalles" | "historial">(
    "detalles",
  );
  const [modalState, setModalState] = useState<
    "idle" | "edit" | "duplicate-flow" | "archive" | "unarchive"
  >("idle");
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [isWaitingForVersion, setIsWaitingForVersion] = useState(false);
  const [showResolutionModal, setShowResolutionModal] = useState(false);

  const { id } = useParams();

  const { data: processData, refetch: refetchProcess } = useGetProcess(id);
  const process = processData?.data;

  const { data: mainFlows, isLoading: isLoadingMainFlows } =
    useGetMainFlowsByProcessId(id);
  const latestVersion = useMemo(() => {
    if (!mainFlows?.data?.length) return 0;
    return Math.max(...mainFlows.data.map((f) => f.version_number || 0));
  }, [mainFlows?.data]);

  // Versión que se está viendo actualmente
  const currentVersion = selectedVersion ?? latestVersion;

  const mainFlow = useMemo(() => {
    if (!mainFlows?.data?.length) return null;
    return (
      mainFlows.data.find((f) => f.version_number === currentVersion) || null
    );
  }, [mainFlows?.data, currentVersion]);

  const duplicatingFlow = useMemo(() => {
    if (!mainFlows?.data?.length) return null;
    return mainFlows.data.find((f) => f.is_duplicating === true) || null;
  }, [mainFlows?.data]);

  const isVersionLocked = useMemo(() => {
    if (!mainFlow) return false;
    return (
      mainFlow.review_status === "PENDING" ||
      mainFlow.review_status === "IN_PROCESS"
    );
  }, [mainFlow]);

  const { mutateAsync: updateFlowRevision, isPending: isSendingResolution } =
    useUpdateFlowRevision(mainFlow?.id || "");

  const currentRevision = useMemo(() => {
    if (!mainFlow?.revisions?.length) return null;
    return mainFlow.revisions.reduce((latest, current) => {
      return new Date(current.created_at) > new Date(latest.created_at)
        ? current
        : latest;
    });
  }, [mainFlow?.revisions]);

  const hasPublishedVersion = useMemo(() => {
    if (!mainFlows?.data?.length) return false;
    const result = mainFlows.data.some((f) => f.status === "PUBLISHED");
    return result;
  }, [mainFlows?.data]);

  const { mutateAsync: sendToApproval, isPending: isSendingToApproval } =
    useSendFlowToApproval(mainFlow?.id || "");

  const { mutateAsync: mutateProcess, isPending: isUpdateLoading } =
    useUpdateProcess();

  const { mutateAsync: archiveProcessMutate, isPending: isArchiveLoading } =
    useArchiveProcess();

  const canBePublish = process?.can_be_publish ?? false;

  const { showToast } = useToast();

  const handleFlowDuplicationCompleted = useCallback(() => {
    //const data = payload as { flow_name?: string } | undefined;
    showToast({
      title: "Nueva versión completada",
      description: `La nueva versión se ha creado exitosamente.`,
      options: { type: "success" },
    });

    setIsWaitingForVersion(false);
    queryClient.invalidateQueries({ queryKey: ["main-flows", id] });
    queryClient.invalidateQueries({ queryKey: ["flows"] });
    queryClient.invalidateQueries({
      queryKey: ["admin-process-detail", id],
    });
  }, [showToast, queryClient, id]);

  useReverb({
    onFlowDuplicationCompleted: handleFlowDuplicationCompleted,
  });

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const navigate = useNavigate();

  const handleApproved = async (
    id: string,
    currentStatus: ProcessStatus,
    reason?: string,
  ) => {
    const nextStatus = STATUS_CONFIG[currentStatus]?.next ?? "ACTIVE";
    const translates = {
      ON_CONSTRUCTION: "activado",
      PUBLISHED: "archivado",
      INACTIVE: "desarchivado",
    };
    try {
      // Para desarchivar (INACTIVE -> PUBLISHED), usar el nuevo endpoint con reason
      if (currentStatus === "INACTIVE") {
        await archiveProcessMutate({
          id,
          payload: { status: nextStatus, reason },
        });
      } else {
        // Para aprobar proceso (ON_CONSTRUCTION -> PUBLISHED), usar el endpoint anterior
        await mutateProcess({
          id,
          payload: { status: nextStatus },
        });
      }
      showToast({
        title: `Proceso ${translates[currentStatus]}`,
        description: `El proceso ha sido ${translates[currentStatus]} correctamente.`,
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error: unknown) {
      // Check for specific error code from backend
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data &&
        error.response.data.error === "PROCESS_NO_APPROVED_FLOWS"
      ) {
        const errorData = error.response.data as unknown as { message: string };
        showToast({
          title: `Error al ${translates[currentStatus]}`,
          description: errorData.message,
          options: {
            type: "error",
            duration: 5000,
          },
        });
      } else {
        showToast({
          title: `Error al ${translates[currentStatus]}`,
          description: `No se pudo ${translates[currentStatus]} el proceso. Intenta nuevamente.`,
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    }
  };

  const getProcessAction = (
    status: ProcessStatus | undefined,
    canBePublish: boolean,
  ) => {
    if (!status) return null;

    if (status === "ON_CONSTRUCTION" && canBePublish && hasPublishedVersion) {
      return {
        label: "Activar proceso",
        onClick: () => handleApproved(id as string, status),
      };
    } else if (canCreate && status !== "ON_CONSTRUCTION") {
      return {
        label: STATUS_CONFIG[status]?.label ?? "",
        onClick: () =>
          setModalState(status === "PUBLISHED" ? "archive" : "unarchive"),
      };
    } else {
      return null;
    }
  };

  const processAction = getProcessAction(process?.status, canBePublish);

  useEffect(() => {
    const saved = sessionStorage.getItem(`process-${id}-version`);
    if (saved) {
      setSelectedVersion(Number(saved));
    }
  }, [id]);

  // Guardar en sessionStorage cuando cambia
  useEffect(() => {
    if (selectedVersion !== null) {
      sessionStorage.setItem(`process-${id}-version`, String(selectedVersion));
    } else {
      sessionStorage.removeItem(`process-${id}-version`);
    }
  }, [selectedVersion, id]);

  useEffect(() => {
    if (type === "historial") {
      setActiveTab("historial");
      const params = new URLSearchParams(searchParams);
      params.delete("type");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [type, searchParams, navigate]);

  useEffect(() => {
    if (
      selectedVersion !== null &&
      mainFlows?.data?.length &&
      !isWaitingForVersion
    ) {
      const versionExists = mainFlows.data.some(
        (f) => f.version_number === selectedVersion,
      );
      if (!versionExists) {
        setSelectedVersion(null); // Vuelve a mostrar la última versión
      }
    }
  }, [selectedVersion, mainFlows?.data, isWaitingForVersion]);

  useEffect(() => {
    if (
      isWaitingForVersion &&
      selectedVersion !== null &&
      mainFlows?.data?.length
    ) {
      const versionExists = mainFlows.data.some(
        (f) => f.version_number === selectedVersion,
      );
      if (versionExists) {
        setIsWaitingForVersion(false);
      }
    }
  }, [isWaitingForVersion, selectedVersion, mainFlows?.data]);

  // TODO: Calcular estas condiciones con los flujos de la versión
  const hasOrphanFlows = useMemo(() => {
    return allFlowsInVersion.some((f) => !f.is_main_flow && !f.parent_flow_id);
  }, [allFlowsInVersion]);

  const hasUnvalidatedFlows = useMemo(() => {
    return allFlowsInVersion.some(
      (f) => f.review_status === "PENDING" || f.review_status === "IN_PROCESS",
    );
  }, [allFlowsInVersion]);

  const hasErrorFlows = useMemo(() => {
    return allFlowsInVersion.some((f) => f.review_status === "DENIED");
  }, [allFlowsInVersion]);

  const hasFlowsInDraft = useMemo(() => {
    return allFlowsInVersion.some((f) => f.review_status === "NOT_STARTED");
  }, [allFlowsInVersion]);

  const revisionCompleted =
    currentRevision?.status === "APPROVED" ||
    currentRevision?.status === "DENIED";

  // Mostrar "Enviar a aprobación" solo si el flujo principal está en Borrador y fue validado
  const showSendToApproval =
    mainFlow?.review_status === "NOT_STARTED" || hasFlowsInDraft;
  const showSendResolution = mainFlow?.review_status !== "NOT_STARTED";
  // Habilitar "Enviar resolución" solo si no hay huérfanos ni sin validar
  const canSendResolution =
    !hasOrphanFlows && !hasUnvalidatedFlows && !revisionCompleted;
  const handleSendToApproval = async () => {
    if (!mainFlow?.id) return;

    try {
      await sendToApproval();
      refetchProcess();
      showToast({
        title: "Versión enviada a aprobación",
        description: "Todos los flujos han sido enviados a aprobación.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      console.error("Error sending to approval:", error);
      showToast({
        title: "Error al enviar a aprobación",
        description:
          "Ha ocurrido un error al intentar enviar la versión a aprobación.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const handleSendResolution = async (data: {
    veredict: string;
    comment: string;
  }) => {
    if (!currentRevision?.id) {
      showToast({
        title: "Error",
        description: "No se encontró la revisión del flujo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      return;
    }

    try {
      await updateFlowRevision({
        revisionId: currentRevision.id,
        action: data.veredict as "APPROVED" | "DENIED",
        comments: [data.comment],
      });

      // Invalidar queries para refrescar datos
      queryClient.invalidateQueries({ queryKey: ["main-flows"] });
      queryClient.invalidateQueries({ queryKey: ["flows"] });

      setShowResolutionModal(false);

      showToast({
        title:
          data.veredict === "APPROVED"
            ? "Versión aprobada"
            : "Versión rechazada",
        description: "La resolución ha sido enviada correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (error) {
      console.error("Error sending resolution:", error);
      showToast({
        title: "Error al enviar resolución",
        description: "Ha ocurrido un error. Intenta nuevamente.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Tabs */}
      <div className="flex w-full justify-between items-center border-b border-neutral">
        <Heading variant="h6">{process?.name}</Heading>
        <div className="flex gap-2 items-center">
          <PageTabs
            tabs={[
              { label: "Detalles", key: "detalles" },
              { label: "Historial", key: "historial" },
            ]}
            activeKey={activeTab}
            onTabChange={(key) => setActiveTab(key as "detalles" | "historial")}
            className="border-b-0 pb-0!"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="tertiary"
                size="icon"
                aria-label="Opciones"
                type="button"
              >
                <Icon name="ellipsis-vertical" type="far" className="text-sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-55.5 min-w-56 rounded-lg border-neutral-silent text-body font-normal p-2 shadow-neutral-xl"
              side="left"
              align="start"
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                  asChild
                >
                  <button
                    type="button"
                    className="w-full"
                    onClick={() => {
                      setModalState("edit");
                    }}
                  >
                    Editar detalles
                  </button>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                  asChild
                >
                  <button
                    type="button"
                    className="w-full text-destructive"
                    onClick={() => {
                      setModalState("duplicate-flow");
                    }}
                  >
                    Duplicar proceso
                  </button>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              {/* Enviar a aprobación - requiere flows:request-review */}
              {showSendToApproval && canRequestReview && (
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                    asChild
                    // disabled={!canSendToApproval || isSendingToApproval}
                  >
                    <button
                      type="button"
                      className="w-full"
                      onClick={handleSendToApproval}
                      disabled={isSendingToApproval}
                    >
                      {isSendingToApproval
                        ? "Enviando..."
                        : "Enviar a aprobación"}
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              )}
              {/* Enviar resolución - requiere flows:perform-review */}
              {showSendResolution && canPerformReview && (
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                    asChild
                    disabled={!canSendResolution}
                  >
                    <button
                      type="button"
                      className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setShowResolutionModal(true)}
                      disabled={!canSendResolution}
                    >
                      Enviar resolución
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              )}
              {processAction && (
                <>
                  <DropdownMenuSeparator className="bg-(--border-neutral-quiet) my-2.5" />

                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                      asChild
                    >
                      <button
                        onClick={processAction.onClick}
                        type="button"
                        className="w-full text-destructive"
                        disabled={isUpdateLoading || isArchiveLoading}
                      >
                        {processAction.label}
                      </button>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {modalState === "edit" && (
        <EditProcessModal
          isOpen={modalState === "edit"}
          modalData={process}
          processId={id || ""}
          onClose={() => setModalState("idle")}
          onSuccess={() => {
            refetchProcess();
          }}
        />
      )}

      {modalState === "duplicate-flow" && (
        <DuplicateProcessModal
          processId={id || ""}
          modalData={process}
          isOpen={modalState === "duplicate-flow"}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}

      {modalState === "archive" && (
        <ArchiveProcessModal
          isOpen={modalState === "archive"}
          onClose={() => setModalState("idle")}
          processId={id || ""}
        />
      )}

      {modalState === "unarchive" && (
        <ConfirmUnArchiveModal
          open={modalState === "unarchive"}
          onClose={() => setModalState("idle")}
          onDelete={async (id, _status, reason) => {
            await handleApproved(id as string, "INACTIVE", reason);
            setModalState("idle");
          }}
          id={id || ""}
        />
      )}

      {showResolutionModal && (
        <SendResolutionModal
          isOpen={showResolutionModal}
          onClose={() => setShowResolutionModal(false)}
          onSubmit={handleSendResolution}
          isLoading={isSendingResolution}
          hasErrorFlows={hasErrorFlows}
        />
      )}

      {/* Contenido dinámico */}
      {activeTab === "detalles" ? (
        <Detalle
          processId={id}
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          latestVersion={latestVersion}
          onFlowsChange={setAllFlowsInVersion}
          setIsWaitingForVersion={setIsWaitingForVersion}
          currentRevision={currentRevision}
          isVersionLocked={isVersionLocked}
          duplicatingFlow={duplicatingFlow}
          isLoadingMainFlows={isLoadingMainFlows}
        />
      ) : (
        <Historial processId={id} />
      )}
    </div>
  );
};
